// app/src/db/idb.ts — offline-first storage (Plan §8).
// Two stores: `catalogue` (branch product snapshot) and `outbox` (sales awaiting sync).

import type { ProductView, CheckoutRequest } from "../../../shared/types";

const DB_NAME = "your-epos";
const DB_VERSION = 1;

export interface OutboxItem {
  device_sale_ref: string;          // local primary key
  payload: CheckoutRequest;
  status: "pending" | "synced" | "conflict";
  conflict_kind?: string;
  receipt_no?: string;
  created_at_ms: number;
}

function open(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains("catalogue")) db.createObjectStore("catalogue", { keyPath: "key" });
      if (!db.objectStoreNames.contains("outbox")) {
        const s = db.createObjectStore("outbox", { keyPath: "device_sale_ref" });
        s.createIndex("status", "status", { unique: false });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function tx<T>(store: string, mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return open().then((db) => new Promise<T>((resolve, reject) => {
    const t = db.transaction(store, mode);
    const req = fn(t.objectStore(store));
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  }));
}

// ── Catalogue cache ──────────────────────────────────────────────────────
export async function saveCatalogue(branchId: string, products: ProductView[], syncedAt: number): Promise<void> {
  await tx("catalogue", "readwrite", (s) => s.put({ key: `branch:${branchId}`, products, syncedAt }));
}
export async function loadCatalogue(branchId: string): Promise<{ products: ProductView[]; syncedAt: number } | null> {
  const row = await tx<any>("catalogue", "readonly", (s) => s.get(`branch:${branchId}`));
  return row ? { products: row.products, syncedAt: row.syncedAt } : null;
}

// ── Outbox ───────────────────────────────────────────────────────────────
export async function enqueueSale(item: OutboxItem): Promise<void> {
  await tx("outbox", "readwrite", (s) => s.put(item));
}
export async function updateOutbox(item: OutboxItem): Promise<void> {
  await tx("outbox", "readwrite", (s) => s.put(item));
}
export async function getPending(): Promise<OutboxItem[]> {
  const all = await tx<OutboxItem[]>("outbox", "readonly", (s) => s.getAll());
  return all.filter((i) => i.status === "pending");
}
export async function countPending(): Promise<number> {
  return (await getPending()).length;
}
export async function clearSynced(): Promise<void> {
  const all = await tx<OutboxItem[]>("outbox", "readonly", (s) => s.getAll());
  await Promise.all(all.filter((i) => i.status === "synced").map((i) =>
    tx("outbox", "readwrite", (s) => s.delete(i.device_sale_ref))));
}
